import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Auth } from 'aws-amplify';

import { UserService } from '../../../core/api/user.service';
import { ErrorHelperService } from '../../../core/helper/error-helper.service';
import { RegexConstants } from '../../../shared/constants/regex.constants';
import { OtpDialogComponent } from '../../../shared/dialogs/otp-dialog/otp-dialog.component';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { selectAppConfigState } from '../../../store/app-config/app-config.selectors';
import { login } from '../../../store/auth/auth.actions';
import { AbstractAuthComponent } from '../abstract-auth.component';

@Component({
  selector: 'fc-auth-login',
  templateUrl: './login.component.html',
  styleUrls: ['../auth.component.scss'],
})
export class LoginComponent extends AbstractAuthComponent implements OnInit {
  helpEmail: string = '';
  helpPhone: string = '';
  loginError: boolean = false;
  loginEmailErrorMessage?: string = undefined;
  loginForm: FormGroup = new FormGroup({
    email: new FormControl(this.email, [Validators.required, Validators.pattern(RegexConstants.EMAIL)]),
    password: new FormControl(this.password, [Validators.required]),
  });
  showPassword: boolean = false;
  showResend = false;
  showResendSent = false;

  destroyRef = inject(DestroyRef);

  constructor(
    readonly dialog: MatDialog,
    readonly errorHelperService: ErrorHelperService,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly spinnerService: SpinnerService,
    readonly store: Store<fromApp.AppState>,
    readonly userService: UserService
  ) {
    super();
    this.loginForm.controls.email.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: value => {
        this.email = value;
        this.loginEmailErrorMessage = this.errorHelperService.getEmailErrorMessage(this.loginForm.controls.email);
      },
    });
    this.loginForm.controls.password.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: value => {
        this.password = value;
      },
    });
    this.authModeChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loginForm.controls.email.setValue(this.email, {
          emitEvent: false,
        });
        this.loginForm.controls.password.setValue(this.password, {
          emitEvent: false,
        });
      },
    });

    this.store
      .select(selectAppConfigState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appConfig => {
        this.helpEmail = appConfig.config['fc_help_email'];
        this.helpPhone = appConfig.config['fc_help_phone'];
      });
  }

  ngOnInit(): void {
    if (this.email) {
      this.loginForm.controls.email.setValue(this.email, { emitEvent: false });
    }
    if (this.password) {
      this.loginForm.controls.password.setValue(this.password, {
        emitEvent: false,
      });
    }
  }

  storeLoginToken(idToken: string, tokenExp: number): void {
    // if Cognito gave us a token first check to see if the user is valid
    // in our database before we save the token and consider the user logged in
    this.userService.isValid(idToken).subscribe((resp: any) => {
      if (resp instanceof HttpErrorResponse) {
        this.errorMessage = 'System Unavailable, please try again later.';
        this.loginError = true;
      } else if (resp['status'].toLowerCase() === 'error') {
        this.errorMessage = resp['message'];
        this.loginError = true;
      } else if (resp['status'].toLowerCase() === 'ok') {
        this.store.dispatch(login({ data: { idToken, tokenExp } }));
        this.loginError = false;
      }
    });
  }

  login() {
    this.spinnerService.show('auth-spinner');
    const email = this.loginForm.value.email.toLowerCase();
    Auth.signIn(email, this.loginForm.value.password)
      .then((cognitoResponse: any) => {
        this.cognitoUser = cognitoResponse;
        if (cognitoResponse.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.cognitoUser = cognitoResponse;
          this.router.navigate(['/create-account'], {
            queryParams: {
              username: this.loginForm.value.email.toLowerCase(),
            },
          });
        } else if (cognitoResponse.challengeName === 'SMS_MFA') {
          this.dialog
            .open(OtpDialogComponent, {
              data: {
                mode: 'authenticate',
                email,
                password: this.loginForm.value.password,
                user: cognitoResponse,
                verificationCodeSent: true,
              },
            })
            .afterClosed()
            .subscribe((response: any) => {
              if (response) {
                this.storeLoginToken(
                  response.signInUserSession.idToken.jwtToken,
                  response.signInUserSession.idToken.payload.exp
                );
              } else {
                this.loginError = true;
                this.errorMessage = 'We were unable to verify your sign in request; please try again.';
              }
            });
        } else {
          this.storeLoginToken(
            cognitoResponse.signInUserSession.idToken.jwtToken,
            cognitoResponse.signInUserSession.idToken.payload.exp
          );
        }
        this.loginError = false;
      })
      .catch((err: any) => {
        if (!err.message.startsWith('Temporary password has expired')) {
          this.loginError = true;
          console.log('Error SignIn:', err);
        }
      })
      .finally(() => {
        this.spinnerService.hide('auth-spinner');
      });
  }
}
